import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { listDocumentMeta } from '../lib/docMeta';
import { allFolders } from '../lib/folders';
import { planSite, publishSite } from '../lib/publishSite';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';
import { Modal } from './Modal';

/**
 * Publish a folder of notes as one small site.
 *
 * A folder, rather than a hand-picked set, because a folder is a grouping
 * people already keep — a handbook, a project, a wiki — and because the thing
 * being published should be something that can be published again tomorrow
 * without rebuilding a selection by hand.
 *
 * The address is shown while it is typed: what is being made here is a public
 * URL, and that is worth seeing before pressing the button.
 */

/** A folder name as a first suggestion for the address. */
function suggestSlug(folder: string): string {
  return folder
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{Mark}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export function PublishSiteDialog() {
  const t = useT();
  const open = useStore((s) => s.publishSiteOpen);
  const setOpen = useStore((s) => s.setPublishSiteOpen);
  const docs = useLiveQuery(() => listDocumentMeta(), [], []);
  const folders = useMemo(() => allFolders(docs), [docs]);

  const [folder, setFolder] = useState('');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  // Pick the first folder as soon as there is one, so the dialog is usable
  // without a choice being made first.
  useEffect(() => {
    if (!open || folder || folders.length === 0) return;
    const first = folders[0]!;
    setFolder(first);
    setTitle(first);
    setSlug(suggestSlug(first));
  }, [open, folders, folder]);

  useEffect(() => {
    if (!folder) return setCount(0);
    void planSite(folder).then((plan) => setCount(plan.pages.length));
  }, [folder]);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    setProblem(null);
    setUrl(null);
  };

  const chooseFolder = (next: string) => {
    setFolder(next);
    setTitle(next);
    setSlug(suggestSlug(next));
  };

  const publish = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const plan = await planSite(folder);
      if (plan.pages.length === 0) return setProblem(t('publish.empty'));
      const result = await publishSite(
        { title: title.trim() || folder, slug: slug.trim(), pages: plan.pages },
        title.trim() || folder,
      );
      if (result.ok) setUrl(result.site.url);
      else setProblem(t(`publish.error.${result.error}` as 'publish.error.failed'));
    } finally {
      setBusy(false);
    }
  };

  const valid =
    folder !== '' && /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/.test(slug) && !slug.includes('--');

  return (
    <Modal onClose={close} title={t('publish.title')}>
      {url ? (
        <>
          <p className="mb-modal-note">{t('publish.done', { n: count })}</p>
          <div className="mb-modal-urlrow">
            <input
              readOnly
              value={url}
              className="mb-modal-url"
              onFocus={(e) => e.target.select()}
            />
            <button className="mb-btn" onClick={() => void navigator.clipboard?.writeText(url)}>
              {t('common.copy')}
            </button>
          </div>
          <p className="mb-modal-note">{t('publish.takeDown')}</p>
          <div className="mb-modal-actions">
            <a className="mb-btn" href={url} target="_blank" rel="noreferrer">
              {t('share.openLink')}
            </a>
            <button className="mb-btn mb-btn-primary" onClick={close}>
              {t('common.done')}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="mb-modal-note">{t('publish.hint')}</p>
          {folders.length === 0 ? (
            <p className="mb-modal-note">{t('publish.noFolders')}</p>
          ) : (
            <>
              <label className="mb-field">
                <span>{t('publish.folder')}</span>
                <select value={folder} onChange={(e) => chooseFolder(e.target.value)}>
                  {folders.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mb-field">
                <span>{t('publish.name')}</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} />
              </label>
              <label className="mb-field">
                <span>{t('publish.address')}</span>
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  spellCheck={false}
                  autoComplete="off"
                />
              </label>
              <p className="mb-modal-note">
                {t('publish.preview', { url: `${window.location.origin}/p/${slug || '…'}` })}
              </p>
              <p className="mb-modal-note">{t('publish.pages', { n: count })}</p>
            </>
          )}
          {problem && <p className="mb-import-error">{problem}</p>}
          <div className="mb-modal-actions">
            <button className="mb-btn" onClick={close}>
              {t('common.cancel')}
            </button>
            <button
              className="mb-btn mb-btn-primary"
              disabled={busy || !valid || count === 0}
              onClick={() => void publish()}
            >
              {busy ? t('publish.publishing') : t('publish.publish')}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
